import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Edit2, Save } from 'lucide-react';
import { Button } from '../ui/Button';
import { supabase } from '../../lib/supabase';
/**
 * Template editor for habit groups
 * Allows users to create/edit template steps that get cloned for each recurrence
 */
export function HabitTemplateEditor({ habitGroupId, habitGroupName, isOpen, onClose, onSave, }) {
    const [templateSteps, setTemplateSteps] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [editingStepId, setEditingStepId] = useState(null);
    const [newStepName, setNewStepName] = useState('');
    const [newStepDuration, setNewStepDuration] = useState(5);
    const [newStepColor, setNewStepColor] = useState('#3B82F6');
    const [editStepData, setEditStepData] = useState({});
    // Breath template management
    const [selectedStepForBreaths, setSelectedStepForBreaths] = useState(null);
    const [breaths, setBreaths] = useState([]);
    const [newBreathName, setNewBreathName] = useState('');
    const [editingBreathId, setEditingBreathId] = useState(null);
    const [editBreathData, setEditBreathData] = useState({});
    // Fetch template steps on open
    useEffect(() => {
        if (isOpen) {
            fetchTemplateSteps();
        }
    }, [isOpen, habitGroupId]);
    const fetchTemplateSteps = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const { data, error: fetchError } = await supabase
                .from('steps')
                .select('*')
                .eq('length_id', habitGroupId)
                .eq('is_template', true)
                .is('instance_date', null)
                .order('created_at', { ascending: true });
            if (fetchError)
                throw fetchError;
            setTemplateSteps(data || []);
        }
        catch (err) {
            setError('Failed to load template steps');
            console.error('Fetch error:', err);
        }
        finally {
            setIsLoading(false);
        }
    };
    const handleAddStep = async () => {
        if (!newStepName.trim())
            return;
        try {
            setIsLoading(true);
            setError(null);
            const { data, error: insertError } = await supabase
                .from('steps')
                .insert({
                user_id: (await supabase.auth.getUser()).data.user?.id,
                length_id: habitGroupId,
                label: newStepName.trim(),
                duration: newStepDuration,
                color: newStepColor,
                is_template: true,
                instance_date: null,
                template_step_id: null,
            })
                .select()
                .single();
            if (insertError)
                throw insertError;
            setTemplateSteps([...templateSteps, data]);
            setNewStepName('');
            setNewStepDuration(5);
            setNewStepColor('#3B82F6');
        }
        catch (err) {
            setError('Failed to add template step');
            console.error('Add error:', err);
        }
        finally {
            setIsLoading(false);
        }
    };
    const handleEditStep = (step) => {
        setEditingStepId(step.id);
        setEditStepData({ ...step });
        // Also open breath templates for this step
        fetchTemplateBreaths(step);
    };
    const handleSaveEdit = async (stepId) => {
        if (!editStepData.label?.trim())
            return;
        try {
            setIsLoading(true);
            setError(null);
            const { error: updateError } = await supabase
                .from('steps')
                .update({
                label: editStepData.label,
                duration: editStepData.duration,
                color: editStepData.color,
            })
                .eq('id', stepId);
            if (updateError)
                throw updateError;
            setTemplateSteps(templateSteps.map(s => (s.id === stepId ? { ...s, ...editStepData } : s)));
            setEditingStepId(null);
            setEditStepData({});
        }
        catch (err) {
            setError('Failed to save changes');
            console.error('Save error:', err);
        }
        finally {
            setIsLoading(false);
        }
    };
    const handleDeleteStep = async (stepId) => {
        if (!confirm('Delete this template step? Instances already created will not be affected.'))
            return;
        try {
            setIsLoading(true);
            setError(null);
            // Delete associated template breaths first
            const { error: breathError } = await supabase
                .from('breaths')
                .delete()
                .eq('step_id', stepId)
                .eq('is_template', true);
            if (breathError)
                throw breathError;
            // Delete the step
            const { error: stepError } = await supabase
                .from('steps')
                .delete()
                .eq('id', stepId);
            if (stepError)
                throw stepError;
            setTemplateSteps(templateSteps.filter(s => s.id !== stepId));
        }
        catch (err) {
            setError('Failed to delete step');
            console.error('Delete error:', err);
        }
        finally {
            setIsLoading(false);
        }
    };
    // Breath template management functions
    const fetchTemplateBreaths = async (step) => {
        try {
            setSelectedStepForBreaths(step);
            const { data, error } = await supabase
                .from('breaths')
                .select('*')
                .eq('step_id', step.id)
                .eq('is_template', true)
                .is('instance_date', null)
                .order('position', { ascending: true });
            if (error)
                throw error;
            setBreaths(data || []);
        }
        catch (err) {
            setError('Failed to load breaths');
            console.error('Fetch breaths error:', err);
        }
    };
    const handleAddBreath = async () => {
        if (!newBreathName.trim() || !selectedStepForBreaths)
            return;
        try {
            setIsLoading(true);
            const nextPosition = (breaths?.length || 0) + 1;
            const { data, error } = await supabase
                .from('breaths')
                .insert({
                step_id: selectedStepForBreaths.id,
                name: newBreathName.trim(),
                position: nextPosition,
                is_template: true,
                instance_date: null,
                user_id: (await supabase.auth.getUser()).data.user?.id,
                completed: false,
                is_running: false,
            })
                .select()
                .single();
            if (error)
                throw error;
            setBreaths([...breaths, data]);
            setNewBreathName('');
        }
        catch (err) {
            setError('Failed to add breath');
            console.error('Add breath error:', err);
        }
        finally {
            setIsLoading(false);
        }
    };
    const handleEditBreath = (breath) => {
        setEditingBreathId(breath.id);
        setEditBreathData({ ...breath });
    };
    const handleSaveBreathEdit = async (breathId) => {
        if (!editBreathData.name?.trim())
            return;
        try {
            setIsLoading(true);
            const { error } = await supabase
                .from('breaths')
                .update({
                name: editBreathData.name,
            })
                .eq('id', breathId);
            if (error)
                throw error;
            setBreaths(breaths.map(b => (b.id === breathId ? { ...b, ...editBreathData } : b)));
            setEditingBreathId(null);
            setEditBreathData({});
        }
        catch (err) {
            setError('Failed to save breath');
            console.error('Save breath error:', err);
        }
        finally {
            setIsLoading(false);
        }
    };
    const handleDeleteBreath = async (breathId) => {
        if (!confirm('Delete this breath?'))
            return;
        try {
            setIsLoading(true);
            const { error } = await supabase.from('breaths').delete().eq('id', breathId);
            if (error)
                throw error;
            setBreaths(breaths.filter(b => b.id !== breathId));
        }
        catch (err) {
            setError('Failed to delete breath');
            console.error('Delete breath error:', err);
        }
        finally {
            setIsLoading(false);
        }
    };
    const handleClose = () => {
        setEditingStepId(null);
        setEditStepData({});
        setNewStepName('');
        setSelectedStepForBreaths(null);
        setBreaths([]);
        onSave?.();
        onClose();
    };
    if (!isOpen)
        return null;
    return (_jsxs("div", { className: "fixed inset-0 z-50 flex items-center justify-center", children: [_jsx("div", { className: "absolute inset-0 bg-black/50", onClick: handleClose }), _jsxs("div", { className: "relative w-full max-w-2xl mx-4 rounded-lg p-6 max-h-[90vh] overflow-y-auto", style: { backgroundColor: 'var(--bg-elevated)' }, children: [_jsxs("div", { className: "flex items-center justify-between mb-6", children: [_jsxs("div", { children: [_jsx("h2", { className: "text-2xl font-light", style: { color: 'var(--text-primary)' }, children: "Template Editor" }), _jsx("p", { className: "text-sm mt-1", style: { color: 'var(--text-secondary)' }, children: habitGroupName })] }), _jsx("button", { onClick: handleClose, className: "p-1 rounded hover:bg-opacity-80", style: { color: 'var(--text-secondary)' }, children: _jsx(X, { className: "w-5 h-5" }) })] }), _jsx("div", { className: "mb-6 p-4 rounded-lg", style: { backgroundColor: 'var(--bg-base)' }, children: _jsx("p", { className: "text-sm", style: { color: 'var(--text-secondary)' }, children: "Create template steps that will be cloned for each day this habit recurs. Users can customize or skip instances as needed." }) }), error && (_jsx("div", { className: "mb-4 p-3 rounded-lg bg-red-500/10", children: _jsx("p", { className: "text-sm text-red-600", children: error }) })), _jsxs("div", { className: "mb-6 space-y-3", children: [_jsx("h3", { className: "text-sm font-medium", style: { color: 'var(--text-primary)' }, children: "Add Template Step" }), _jsxs("div", { className: "space-y-3", children: [_jsx("input", { type: "text", value: newStepName, onChange: e => setNewStepName(e.target.value), placeholder: "Step name", className: "w-full px-4 py-2 rounded-lg text-sm", style: {
                                            backgroundColor: 'var(--bg-base)',
                                            border: '1px solid var(--border-color)',
                                            color: 'var(--text-primary)',
                                        } }), _jsxs("div", { className: "grid grid-cols-2 gap-3", children: [_jsx("input", { type: "number", value: newStepDuration, onChange: e => setNewStepDuration(Math.max(1, parseInt(e.target.value) || 1)), placeholder: "Duration (minutes)", min: "1", className: "px-4 py-2 rounded-lg text-sm", style: {
                                                    backgroundColor: 'var(--bg-base)',
                                                    border: '1px solid var(--border-color)',
                                                    color: 'var(--text-primary)',
                                                } }), _jsx("input", { type: "color", value: newStepColor, onChange: e => setNewStepColor(e.target.value), className: "px-4 py-2 rounded-lg text-sm cursor-pointer", style: {
                                                    backgroundColor: 'var(--bg-base)',
                                                    border: '1px solid var(--border-color)',
                                                } })] }), _jsxs(Button, { onClick: handleAddStep, disabled: !newStepName.trim() || isLoading, style: { backgroundColor: newStepColor, color: 'white' }, children: [_jsx(Plus, { className: "w-4 h-4 mr-2" }), "Add Step"] })] })] }), _jsxs("div", { className: "space-y-3", children: [_jsxs("h3", { className: "text-sm font-medium", style: { color: 'var(--text-primary)' }, children: ["Template Steps (", templateSteps.length, ")"] }), templateSteps.length === 0 ? (_jsx("div", { className: "p-6 rounded-lg text-center", style: { backgroundColor: 'var(--bg-base)' }, children: _jsx("p", { className: "text-sm", style: { color: 'var(--text-secondary)' }, children: "No template steps yet. Add your first one above!" }) })) : (_jsx("div", { className: "space-y-2", children: templateSteps.map(step => (_jsx("div", { className: "p-3 rounded-lg transition-all", style: {
                                        backgroundColor: editingStepId === step.id ? `${step.color || '#3B82F6'}20` : 'var(--bg-base)',
                                        border: `1px solid ${editingStepId === step.id ? step.color || '#3B82F6' : 'var(--border-color)'}`,
                                    }, children: editingStepId === step.id ? (
                                    // Edit Mode
                                    _jsxs("div", { className: "space-y-3", children: [_jsx("input", { type: "text", value: editStepData.label || '', onChange: e => setEditStepData({ ...editStepData, label: e.target.value }), className: "w-full px-3 py-2 rounded text-sm", style: {
                                                    backgroundColor: 'var(--bg-elevated)',
                                                    border: '1px solid var(--border-color)',
                                                    color: 'var(--text-primary)',
                                                } }), _jsxs("div", { className: "grid grid-cols-2 gap-3", children: [_jsx("input", { type: "number", value: editStepData.duration || 1, onChange: e => setEditStepData({
                                                            ...editStepData,
                                                            duration: Math.max(1, parseInt(e.target.value) || 1),
                                                        }), min: "1", className: "px-3 py-2 rounded text-sm", style: {
                                                            backgroundColor: 'var(--bg-elevated)',
                                                            border: '1px solid var(--border-color)',
                                                            color: 'var(--text-primary)',
                                                        } }), _jsx("input", { type: "color", value: editStepData.color || '#3B82F6', onChange: e => setEditStepData({ ...editStepData, color: e.target.value }), className: "px-3 py-2 rounded text-sm cursor-pointer", style: {
                                                            backgroundColor: 'var(--bg-elevated)',
                                                            border: '1px solid var(--border-color)',
                                                        } })] }), _jsxs("div", { className: "flex gap-2", children: [_jsxs(Button, { onClick: () => handleSaveEdit(step.id), disabled: isLoading, size: "sm", style: { backgroundColor: '#10B981', color: 'white' }, children: [_jsx(Save, { className: "w-4 h-4" }), "Save"] }), _jsx(Button, { onClick: () => {
                                                            setEditingStepId(null);
                                                            setEditStepData({});
                                                        }, disabled: isLoading, variant: "ghost", size: "sm", children: "Cancel" })] })] })) : (
                                    // View Mode
                                    _jsxs("div", { className: "flex items-center gap-3", children: [_jsx("div", { className: "w-3 h-3 rounded-full", style: { backgroundColor: step.color || '#3B82F6' } }), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("p", { className: "text-sm font-medium", style: { color: 'var(--text-primary)' }, children: step.label }), _jsxs("p", { className: "text-xs", style: { color: 'var(--text-tertiary)' }, children: [step.duration, " minute", step.duration !== 1 ? 's' : ''] })] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("button", { onClick: () => handleEditStep(step), disabled: isLoading, className: "p-1 rounded hover:bg-blue-500/20", title: "Edit", children: _jsx(Edit2, { className: "w-4 h-4", style: { color: '#3B82F6' } }) }), _jsx("button", { onClick: () => handleDeleteStep(step.id), disabled: isLoading, className: "p-1 rounded hover:bg-red-500/20", title: "Delete", children: _jsx(Trash2, { className: "w-4 h-4 text-red-500" }) })] })] })) }, step.id))) }))] }), selectedStepForBreaths && (_jsxs("div", { className: "space-y-3 mt-6 p-4 rounded-lg", style: { backgroundColor: 'var(--bg-base)' }, children: [_jsxs("div", { className: "flex items-center justify-between mb-3", children: [_jsxs("h3", { className: "text-sm font-medium", style: { color: 'var(--text-primary)' }, children: ["Breaths for: ", selectedStepForBreaths.label] }), _jsx("button", { onClick: () => {
                                            setSelectedStepForBreaths(null);
                                            setBreaths([]);
                                        }, className: "text-xs text-blue-500 hover:text-blue-600", children: "Close" })] }), _jsxs("div", { className: "flex gap-2", children: [_jsx("input", { type: "text", value: newBreathName, onChange: e => setNewBreathName(e.target.value), placeholder: "Add breath...", className: "flex-1 px-3 py-2 rounded text-sm", style: {
                                            backgroundColor: 'var(--bg-elevated)',
                                            border: '1px solid var(--border-color)',
                                            color: 'var(--text-primary)',
                                        } }), _jsx(Button, { onClick: handleAddBreath, disabled: !newBreathName.trim() || isLoading, size: "sm", style: { backgroundColor: selectedStepForBreaths.color || '#3B82F6', color: 'white' }, children: _jsx(Plus, { className: "w-4 h-4" }) })] }), breaths.length === 0 ? (_jsx("p", { className: "text-xs text-center", style: { color: 'var(--text-secondary)' }, children: "No breaths yet" })) : (_jsx("div", { className: "space-y-2 max-h-48 overflow-y-auto", children: breaths.map(breath => (_jsx("div", { className: "p-2 rounded flex items-center gap-2", style: {
                                        backgroundColor: editingBreathId === breath.id ? `${selectedStepForBreaths.color || '#3B82F6'}20` : 'var(--bg-elevated)',
                                        border: `1px solid ${editingBreathId === breath.id ? selectedStepForBreaths.color || '#3B82F6' : 'var(--border-color)'}`,
                                    }, children: editingBreathId === breath.id ? (_jsxs(_Fragment, { children: [_jsx("input", { type: "text", value: editBreathData.name || '', onChange: e => setEditBreathData({ ...editBreathData, name: e.target.value }), className: "flex-1 px-2 py-1 rounded text-xs", style: {
                                                    backgroundColor: 'var(--bg-base)',
                                                    border: '1px solid var(--border-color)',
                                                    color: 'var(--text-primary)',
                                                } }), _jsx("button", { onClick: () => handleSaveBreathEdit(breath.id), className: "p-1 rounded hover:bg-green-500/20", disabled: isLoading, children: _jsx(Save, { className: "w-3 h-3 text-green-500" }) }), _jsx("button", { onClick: () => {
                                                    setEditingBreathId(null);
                                                    setEditBreathData({});
                                                }, className: "p-1 rounded hover:bg-gray-500/20", children: _jsx(X, { className: "w-3 h-3", style: { color: 'var(--text-secondary)' } }) })] })) : (_jsxs(_Fragment, { children: [_jsx("span", { className: "flex-1 text-xs", style: { color: 'var(--text-primary)' }, children: breath.name }), _jsx("button", { onClick: () => handleEditBreath(breath), className: "p-1 rounded hover:bg-blue-500/20", disabled: isLoading, title: "Edit", children: _jsx(Edit2, { className: "w-3 h-3 text-blue-500" }) }), _jsx("button", { onClick: () => handleDeleteBreath(breath.id), className: "p-1 rounded hover:bg-red-500/20", disabled: isLoading, title: "Delete", children: _jsx(Trash2, { className: "w-3 h-3 text-red-500" }) })] })) }, breath.id))) }))] })), !selectedStepForBreaths && templateSteps.length > 0 && (_jsx("div", { className: "mt-4 p-3 rounded-lg text-center", style: { backgroundColor: 'var(--bg-base)' }, children: _jsx("p", { className: "text-xs", style: { color: 'var(--text-secondary)' }, children: "Click \"Edit\" on a step to manage its breath templates" }) })), _jsx("div", { className: "mt-6 flex justify-end gap-3", children: _jsx(Button, { onClick: handleClose, variant: "ghost", children: "Close" }) })] })] }));
}
